import React, { useEffect, useState } from "react";
import { currentUrl, unsavedChanges } from "../../App";
import { useLocation } from "react-router-dom";
import { Button, Card, Col, Container, InputGroup, Row, Spinner, Stack, Table } from "react-bootstrap";
import {
  fetchBedChecks,
  fetchBeds,
  fetchFacilities,
  fetchRooms,
  patchRoom,
  postBedCheck,
  updateConsecutiveCount,
} from "../../services/httpClient";
import { isIndividuallyAssignableRoom, sortRoomsForFilter } from "../../helpers/housingAssignment";
import RoomFilterSelect from "./RoomFilterSelect";
import PageTitle from "../ui/PageTitle";
import BedOccupancyCard from "./BedOccupancyCard";
import useApp from "../../utils/useApp";

interface BedOccupancyState {
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  getData: boolean;
  errMsg: any;
  setErrMsg: any;
}

const BedOccupancy = ({ setOrgToSave, setShowToast, setToastText, setToastBody, getData, errMsg, setErrMsg }: BedOccupancyState) => {
  const location = useLocation();
  currentUrl.value = location.pathname;
  const { showMenu }: any = useApp();
  const [filterRoom, setFilterRoom] = useState<any>();
  const [rooms, setRooms] = useState<any[]>([]);
  const [beds, setBeds] = useState<any[]>([]);
  const [filteredBeds, setFilteredBeds] = useState<any[]>([]);
  const [missingBeds, setMissingBeds] = useState<any[]>([]);
  const [resetCards, setResetCards] = useState<boolean>(false);
  const [showMissingReport, setShowMissingReport] = useState<boolean>(false);
  const [bedChecks, setBedChecks] = useState<any[]>([]);
  const [viewOccupied, setViewOccupied] = useState<boolean>(false);
  const [viewAll, setViewAll] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  // rooms whose occupancy check failed to save on the last "All Rooms" submit; a resubmit only retries these
  const [failedRoomIds, setFailedRoomIds] = useState<any[]>([]);

  useEffect(() => {
    if (getData) {
      setIsLoading(true);
      setViewOccupied(false);
      setViewAll(true);
      setFailedRoomIds([]);
      getBeds(false);
    }
  }, [getData]);

  const getBeds = (maintainFilter: boolean) => {
    fetchBeds()
      .then((res: any) => {
        const bedList = res[0].data;
        setResetCards(false);

        bedList.sort((a: any, b: any) => {
          const roomCompare = a.room_key.localeCompare(b.room_key, undefined, {
            sensitivity: "base",
          });
          if (roomCompare !== 0) return roomCompare;

          return a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: "base",
          });
        });
        getRooms(bedList, maintainFilter);
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getRooms = (allBeds: any, maintainFilter: boolean) => {
    Promise.all([fetchRooms(), fetchFacilities()])
      .then(([roomRes, facilityRes]: any) => {
        // only rooms whose beds are assigned one at a time are checked here, not dwellings or family rooms
        // and only if they have beds to check
        const roomIdsWithBeds = new Set(allBeds.map((bed: any) => bed.room_id));
        let roomList = roomRes[0].data.filter(
          (room: any) => roomIdsWithBeds.has(room.id) && isIndividuallyAssignableRoom(room, facilityRes[0].data),
        );
        const assignableRoomIds = new Set(roomList.map((room: any) => room.id));
        const bedList = allBeds.filter((bed: any) => assignableRoomIds.has(bed.room_id));
        setBeds(bedList);
        roomList = roomList.map((room: any) => {
          const bedCount = bedList.filter((bed: any) => bed.room_id === room.id).length;
          return {
            ...room,
            name: `${room.name} (${bedCount} beds)`,
            facility_name: facilityRes[0].data.find((f: any) => f.id === room.property_id)?.name ?? null,
          };
        });

        roomList = sortRoomsForFilter(roomList);

        roomList = roomList.map((room: any) => {
          if (room?.occupancy_check_at) {
            const utcDate = new Date(room?.occupancy_check_at + " UTC");
            const localTime = utcDate.toLocaleString(undefined, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            });
            return { ...room, occupancy_check_at: localTime };
          } else {
            return room;
          }
        });

        setRooms(roomList);

        if (maintainFilter) {
          if (filterRoom === "0") {
            setFilteredBeds(bedList);
          } else {
            const room = roomList.find((room: any) => room.key === filterRoom?.key);
            setFilterRoom(room);
            const filtered = bedList.filter((bed: any) => bed.room_id === filterRoom?.id);
            setFilteredBeds(filtered);
          }
        } else if (roomList.length === 1) {
          setFilterRoom(roomList[0]);
          const roomId = roomList[0].id;
          const filtered = bedList.filter((bed: any) => bed.room_id === roomId);
          setFilteredBeds(filtered);
        } else {
          setFilterRoom(roomList[0]);
          const roomId = roomList[0].id;
          const filtered = bedList.filter((bed: any) => bed.room_id === roomId);
          setFilteredBeds(filtered);
        }
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const filterByRoom = (e: any) => {
    setFailedRoomIds([]);
    if (e?.key === "0") {
      setFilteredBeds(beds);
      setFilterRoom("0");
      return;
    }
    const room = rooms.find((room: any) => room.key === e?.key);
    setFilterRoom(room);
    setMissingBeds([]);
    setResetCards(true);
    setViewOccupied(false);
    setViewAll(true);

    const roomId = rooms.find((room: any) => room.key === e?.key)?.id;
    const filtered = beds.filter((bed: any) => bed.room_id === roomId);
    setFilteredBeds(filtered);
  };

  const onClickSubmit = () => {
    setIsUpdating(true);
    setErrMsg("");
    let failedBeds: any[] = [];

    const updatedRoom = {
      occupancy_check: true,
    };

    // All Rooms
    if (filterRoom === "0") {
      // after a partial failure only the rooms that failed are submitted again
      const roomsToSubmit = failedRoomIds.length > 0 ? rooms.filter((room: any) => failedRoomIds.includes(room.id)) : rooms;
      const failedRooms: any[] = [];
      const savedRoomIds: any[] = [];

      Promise.all(
        roomsToSubmit.map((room: any) =>
          patchRoom(updatedRoom, room.id)
            .then(async () => {
              const roomMissingBeds = missingBeds.filter((b: any) => b.room_id === room.id);

              await Promise.allSettled(
                roomMissingBeds.map((m: any, index: number) => {
                  const bedObject = {
                    room_id: m?.room_id,
                    bed_id: m?.id,
                    occupant_id: m?.occupant_id,
                    occupant_type: m?.occupant_type,
                  };

                  return postBedCheck(bedObject).catch(() => {
                    failedBeds.push(m);
                  });
                }),
              );
              savedRoomIds.push(room.id);
            })
            .catch((err: any) => {
              console.log(`Failed to patch room ${room.name}:`, err?.response?.data?.msg);
              failedRooms.push(room);
            }),
        ),
      )
        .then(async () => {
          // beds in rooms that did not save are left alone, so their counts are not touched
          await updateFailedConsecutiveCount(failedBeds, savedRoomIds);

          if (savedRoomIds.length > 0) {
            const savedMissing = missingBeds.filter((b: any) => savedRoomIds.includes(b.room_id));
            setShowToast(true);
            setToastText("Bed Occupancy Submitted");
            setToastBody(savedMissing.length > 0 ? `${savedMissing.length} Missing` : "No Missing");
          }

          if (failedRooms.length > 0) {
            // keep what the worker marked in the rooms that failed so they can submit again
            const failedIds = failedRooms.map((room: any) => room.id);
            setFailedRoomIds(failedIds);
            setMissingBeds(missingBeds.filter((b: any) => failedIds.includes(b.room_id)));
            unsavedChanges.value = true;
            setOrgToSave(true);
          } else {
            setFailedRoomIds([]);
            unsavedChanges.value = false;
            setOrgToSave(false);
            setMissingBeds([]);
            setResetCards(true);
            setViewOccupied(false);
            setViewAll(true);
          }
          getBeds(true);
          setIsUpdating(false);

          // Error messaging
          const messages: string[] = [];
          if (failedRooms.length > 0) {
            messages.push("Failed to submit occupancy check for: \n" + failedRooms.map((room: any) => room.name).join("\n"));
          }
          if (failedBeds.length === 1) {
            messages.push("Failed to mark missing for bed: " + failedBeds.map((note) => note.name));
          } else if (failedBeds.length > 1) {
            messages.push("Failed to mark missing for beds: \n" + failedBeds.map((note) => note.name).join("\n"));
          }
          if (messages.length > 0) {
            setErrMsg(messages.join("\n"));
          }
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
          setIsUpdating(false);
        });

      return;
    }

    patchRoom(updatedRoom, filterRoom?.id)
      .then(async () => {
        if (missingBeds.length > 0) {
          await Promise.allSettled(
            missingBeds.map((m: any, index: number) => {
              const bedObject = {
                room_id: m?.room_id,
                bed_id: m?.id,
                occupant_id: m?.occupant_id,
                occupant_type: m?.occupant_type,
              };

              return postBedCheck(bedObject)
                .then(async () => {
                  if (missingBeds.length === index + 1) {
                    await updateFailedConsecutiveCount(failedBeds);
                    setShowToast(true);
                    setToastText("Bed Occupancy Submitted");
                    setToastBody(missingBeds.length + " Missing");
                    unsavedChanges.value = false;
                    setOrgToSave(false);
                    setMissingBeds([]);
                    setResetCards(true);
                    setViewOccupied(false);
                    setViewAll(true);
                    getBeds(true);
                  }
                })
                .catch((err: any) => {
                  failedBeds.push(m);
                });
            }),
          );
          setIsUpdating(false);
          // Return the list of failed bed names as an error
          if (failedBeds.length === 1) {
            const failedBedsText = failedBeds.map((note) => note.name);
            setErrMsg("Failed to mark missing for bed: " + failedBedsText);
          } else if (failedBeds.length > 0) {
            const failedBedsText = failedBeds.map((note) => note.name).join("\n");
            setErrMsg("Failed to mark missing for beds: \n" + failedBedsText);
            return;
          }
        } else {
          await updateFailedConsecutiveCount(failedBeds);
          setShowToast(true);
          setToastText("Bed Occupancy Submitted");
          setToastBody("No Missing");
          unsavedChanges.value = false;
          setOrgToSave(false);
          setMissingBeds([]);
          setResetCards(true);
          getBeds(true);
          setIsUpdating(false);
        }
      })
      .catch((err: any) => {
        console.log(err?.response?.data?.msg);
        setErrMsg(err?.response?.data?.msg + ": " + filterRoom.name);
        setIsUpdating(false);
        return;
      });
  };

  const updateFailedConsecutiveCount = async (failedBeds: any[], savedRoomIds?: any[]) => {
    const occupiedBeds = filteredBeds.filter((b: any) => b.occupant_id && (!savedRoomIds || savedRoomIds.includes(b.room_id)));
    await Promise.allSettled(
      occupiedBeds.map((m: any, index: number) => {
        const bedObject = {
          is_missing: missingBeds.some((b: any) => b.id === m.id) && !failedBeds.some((b: any) => b.id === m.id) ? 1 : 0,
          room_id: m?.room_id,
          bed_id: m?.id,
          occupant_id: m?.occupant_id,
          occupant_type: m?.occupant_type,
        };

        return updateConsecutiveCount(bedObject).catch(() => {
          setErrMsg("Failed to update consecutive count for bed: " + m.name);
        });
      }),
    );
  };

  const onClickCancel = () => {
    setErrMsg("");
    setFailedRoomIds([]);
    unsavedChanges.value = false;
    setOrgToSave(false);
    setMissingBeds([]);
    setResetCards(true);
    setViewOccupied(false);
    setViewAll(true);
    getBeds(true);
  };

  const onClickViewReport = () => {
    setMissingBeds([]);
    fetchBedChecks()
      .then((res: any) => {
        const bedList = res[0].data.map((bed: any) => {
          const utcDate = new Date(bed?.created_at + " UTC");
          const localTime = utcDate.toLocaleString(undefined, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
          return { ...bed, created_at: localTime };
        });

        setBedChecks(bedList);
        setShowMissingReport(true);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const onClickViewCheck = () => {
    setShowMissingReport(false);
    setMissingBeds([]);
  };

  const onClickViewOccupied = () => {
    setViewOccupied(true);
    setViewAll(false);
    const filtered = beds.filter((bed: any) => bed.room_id === filterRoom?.id);
    const occupiedBeds = filtered.filter((b: any) => b.occupant_id);
    setFilteredBeds(occupiedBeds);
  };

  const onClickViewAll = () => {
    setViewOccupied(false);
    setViewAll(true);
    const filtered = beds.filter((bed: any) => bed.room_id === filterRoom?.id);
    setFilteredBeds(filtered);
  };

  return showMissingReport === true ? (
    <>
      <PageTitle
        title="Bed Occupancy Report"
        type="section"
        backButtonText="Back to Bed Occupancy"
        onBackClick={() => onClickViewCheck()}
      />
      <Row className="pb-5 mt-2">
        <Col xs={12}>
          <Row>
            <h5 className="app-header-text">Missing Report Last 7 Days</h5>
          </Row>
          {bedChecks.length > 0 ? (
            <>
              <Table striped>
                <thead>
                  <tr className="text-muted">
                    <th>Date</th>
                    <th>Room Name</th>
                    <th>Bed Name</th>
                    <th>Occupant Name</th>
                    <th>Consecutive</th>
                    <th>Submitted By</th>
                  </tr>
                </thead>
                <tbody>
                  {bedChecks?.map((m: any) => (
                    <tr key={m.id}>
                      <td>{m.created_at}</td>
                      <td>{m.room_name}</td>
                      <td>{m.name}</td>
                      <td>{m.firstname + " " + m.lastname}</td>
                      <td>{m.consecutive}</td>
                      <td>{m.created_firstname + " " + m.created_lastname}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          ) : (
            <p className="mt-2">No Missing Reported</p>
          )}
        </Col>
      </Row>
    </>
  ) : (
    <>
      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg.split("\n").map((line: any, index: any) => (
              <span key={index}>
                {line}
                <br />
              </span>
            ))}
          </p>
          <>
            <PageTitle
              title="Bed Occupancy Check"
              type="section"
              buttonColor="secondary"
              buttonTitle="Occupancy Report"
              onButtonClick={() => onClickViewReport()}
            />
            <Row className={showMenu ? "mt-3 sticky-top card-note-menu-open-sticky" : "mt-3 sticky-top card-note-sticky"}>
              <Col xs={12} lg={10}>
                <div style={{ backgroundColor: "#fff" }}>
                  <Card className="bg-light">
                    <Row className="pe-0 mb-3 mt-3 ms-1 me-1">
                      <Col xs={6}>
                        <RoomFilterSelect
                          rooms={rooms}
                          filterRoom={filterRoom}
                          onUpdate={(e: any) => filterByRoom(e)}
                          disabled={missingBeds.length > 0 ? true : false}
                        />
                      </Col>
                      <Col xs={6} className="d-flex justify-content-end align-items-center">
                        <InputGroup className="d-flex justify-content-end align-items-center">
                          <Button
                            className={
                              viewOccupied ? "app-primary-color me-2 button rounded" : "app-primary-color-inverse me-2 button rounded"
                            }
                            onClick={() => onClickViewOccupied()}
                          >
                            Occupied
                          </Button>
                          <Button
                            className={viewAll ? "app-primary-color me-2 button rounded" : "app-primary-color-inverse me-2 button rounded"}
                            onClick={() => onClickViewAll()}
                          >
                            All
                          </Button>
                        </InputGroup>
                      </Col>
                    </Row>
                  </Card>
                </div>
                {filteredBeds.length > 0 && (
                  <Card className="border-0 rounded-0">
                    <Row className="pe-0 mb-3 mt-3 ms-1 me-1">
                      <Col xs={12} className="d-flex justify-content-start align-items-center">
                        <Stack direction="horizontal" gap={3}>
                          <Button className="app-secondary-color button" onClick={() => onClickSubmit()} disabled={isUpdating}>
                            {missingBeds.length > 0 ? "Submit" : "Submit with No Missing"}
                          </Button>
                          <div className="vr" />
                          <Button
                            className="app-primary-color button"
                            onClick={() => onClickCancel()}
                            disabled={missingBeds.length > 0 || failedRoomIds.length > 0 ? false : true}
                          >
                            Cancel
                          </Button>
                          {filterRoom !== "0" && (
                            <p className="app-header-text m-0 p-0">
                              {filterRoom?.occupancy_check_at
                                ? "Last Checked " + filterRoom?.occupancy_check_at
                                : "No Occupancy Check Completed"}{" "}
                              {filterRoom?.firstname && filterRoom?.lastname
                                ? "By " + filterRoom?.firstname + " " + filterRoom?.lastname
                                : ""}{" "}
                            </p>
                          )}
                        </Stack>
                      </Col>
                    </Row>
                  </Card>
                )}
              </Col>
            </Row>
            <Row className="pb-3 mt-0">
              <Col xs={12} lg={10} className="pb-3">
                {filteredBeds.length === 0 ? (
                  <Row className="mt-3">
                    <p className="mt-2">No Beds Created</p>
                  </Row>
                ) : (
                  filteredBeds?.map((item: any, index: number) => (
                    <Row key={index}>
                      <Col xs={12}>
                        <Row className="mb-1"></Row>
                        <BedOccupancyCard
                          item={item}
                          missingBeds={missingBeds}
                          setMissingBeds={setMissingBeds}
                          resetCards={resetCards}
                          setOrgToSave={setOrgToSave}
                        />
                      </Col>
                    </Row>
                  ))
                )}
              </Col>
            </Row>
          </>
        </>
      )}
    </>
  );
};

export default BedOccupancy;
